import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import WidgetCard from '../components/DashboardWidgets/WidgetCard'; // <-- dostosuj ścieżkę jeśli trzeba
import { useLocation } from 'react-router-dom';
import { useCommentData, useCommentMutations, Note } from '../hooks/useCommentData';

const NotesPage: React.FC = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [noteContent, setNoteContent] = useState('');
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);

    // Use optimized hooks for data management
    const { notes, isLoading, error, noteToComment } = useCommentData();
    const { createComment, updateComment, deleteComment, isCreating, isUpdating, isDeleting } = useCommentMutations();

    // Derived state for UI
    const loading = isLoading;
    const saving = isCreating || isUpdating || isDeleting;
    const errorMessage = error ? (error instanceof Error ? error.message : String(error) || 'Wystąpił błąd podczas ładowania notatek') : null;

    const location = useLocation();

    // Przewinięcie i podświetlenie notatki po kliknięciu z widżetu
    useEffect(() => {
        const hash = location.hash || window.location.hash;
        if (hash && notes.length > 0) {
            // Dodaj małe opóźnienie aby upewnić się, że DOM jest gotowy
            const timeoutId = setTimeout(() => {
                const el = document.getElementById(hash.substring(1));
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.classList.add('highlighted');
                    setTimeout(() => {
                        el.classList.remove('highlighted');
                    }, 2500);
                }
            }, 100);
            
            return () => clearTimeout(timeoutId);
        }
    }, [location.hash, notes]);

    const handleOpenDialog = (noteId?: string) => {
        if (noteId) {
            const note = notes.find((n) => n.id === noteId);
            if (note) {
                setNoteContent(note.content);
                setEditingNoteId(note.id);
            }
        } else {
            setNoteContent('');
            setEditingNoteId(null);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setNoteContent('');
        setEditingNoteId(null);
    };

    const handleSaveNote = () => {
        if (!noteContent.trim()) return;

        const noteData = noteToComment({ 
            id: editingNoteId || undefined, 
            content: noteContent.trim() 
        });

        if (editingNoteId) {
            // Update existing note
            updateComment({
                id: parseInt(editingNoteId),
                requestBody: noteData
            });
        } else {
            // Create new note
            createComment({ requestBody: noteData });
        }

        handleCloseDialog();
    };

    const handleDeleteNote = (id: string) => {
        deleteComment({ id: parseInt(id) });
    };

    const groupNotesByDate = (): Record<string, Note[]> => {
        const groups: Record<string, Note[]> = {};
        notes.forEach((note) => {
            const dateKey = format(new Date(note.createdAt), 'yyyy-MM-dd');
            if (!groups[dateKey]) groups[dateKey] = [];
            groups[dateKey].push(note);
        });
        return groups;
    };

    const groupedNotes = groupNotesByDate();

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const monthName = pl.localize?.month(date.getMonth(), { width: 'wide', context: 'standalone' });
        const year = date.getFullYear();
        return `${day} ${monthName} ${year}`;
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 3
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                <Button
                    startIcon={<EditNoteRoundedIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{
                        background: 'linear-gradient(to right, #06b6d4, #2563eb)',
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: '9999px',
                        fontWeight: 400,
                        textTransform: 'none',
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                        '&:hover': {
                            background: 'linear-gradient(to right, #059dc5, #1e4ed8)',
                        },
                    }}
                >
                    Dodaj notatkę
                </Button>
            </Box>

            {/* Error Alert */}
            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                </Alert>
            )}

            {/* Loading State */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {Object.entries(groupedNotes)
                        .sort((a, b) => (a[0] < b[0] ? 1 : -1))
                        .map(([date, notesForDate]) => (
                            <Grid size={{xs: 12, md: 6}} key={date}>
                                <WidgetCard sx={{ height: '100%' }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{
                                            pb: 0.5,
                                            mb: 1.5,
                                            borderBottom: '1px solid #e0e0e0',
                                        }}
                                    >
                                        {formatDate(date)}
                                    </Typography>
                                    <Grid container direction="column" spacing={1}>
                                        {notesForDate.map((note) => (
                                            <Grid key={note.id}>
                                                <Box
                                                    id={note.id}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        p: 0.5,
                                                        scrollMarginTop: '80px',
                                                        '&.highlighted': {
                                                            backgroundColor: '#fff9c4',
                                                            transition: 'background-color 1s ease',
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                            flexGrow: 1,
                                                        }}
                                                    >
                                                        {note.content}
                                                    </Typography>
                                                    <Box sx={{ ml: 1, flexShrink: 0 }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenDialog(note.id)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteNote(note.id)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </WidgetCard>
                            </Grid>
                        ))}
                </Grid>
            )}

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle
                    sx={{
                        fontSize: '1rem', // zmniejszona czcionka (domyślnie jest ~1.25rem)
                        fontWeight: 600,  // subtelne pogrubienie
                    }}
                >
                    {editingNoteId ? 'Edytuj notatkę' : 'Dodaj notatkę'}
                </DialogTitle>

                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Wpisz treść notatki..."
                        value={noteContent}
                        onChange={(e) => {
                            if (e.target.value.length <= 5000) {
                                setNoteContent(e.target.value);
                            }
                        }}
                        helperText={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span></span>
                                <span style={{
                                    color: (noteContent?.length || 0) >= 5000 ? '#d32f2f' : '#666',
                                    fontSize: '0.75rem'
                                }}>
                                    {noteContent?.length || 0}/5000
                                </span>
                            </Box>
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={saving}>Anuluj</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveNote}
                        disabled={!noteContent.trim() || saving}
                        startIcon={saving ? <CircularProgress size={16} /> : undefined}
                    >
                        {saving ? 'Zapisywanie...' : 'Zapisz'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NotesPage;
